import { gql } from '@apollo/client';
import CircularProgress from '@mui/material/CircularProgress';
import { Maybe } from 'graphql/jsutils/Maybe';
import { NextPage } from 'next';
import Router from 'next/router';
import React, {
  FunctionComponent,
  ReactNode,
  useContext,
  useState,
} from 'react';
import styled from 'styled-components';
import { FAQ } from '../components/faq';
import { FOOTER_QUERY, Footer } from '../components/footer';
import { HEADER_QUERY, Header } from '../components/header';
import { Modal } from '../components/modal';
import { Col, ResponsiveContainer, Row } from '../components/responsive';
import { Button } from '../components/ui/button';
import { ButtonGroup } from '../components/ui/buttonGroup';
import { Icon } from '../components/ui/icon';
import { Separator } from '../components/ui/separator';
import { SliderInput } from '../components/ui/sliderInput';
import {
  EApiPackageSlug,
  EPlanSlug,
  ICalculation,
  InitialCalculationsRequest,
  PricingContext,
  PricingContextProvider,
  getPriceString,
  getSavingPercentage,
  million,
  numberToWord,
} from '../contexts/pricing';
import { ContentStackClient } from '../lib/contentstack';
import { Pricing } from '../types/contentstack';
import { IPageProps } from '../types/page';
import { classnames } from '../utils/classnames';
import { getAssetUrl } from '../utils/contentstack';
import { mediaScreen } from '../utils/mediaScreen';
import { fetchCalculations } from './api/calculations';

export const Page: NextPage<IPricingPage> = (props) => {
  return (
    <PricingContextProvider initialCalculations={props.initialCalculations}>
      <PageComponent {...props} />
    </PricingContextProvider>
  );
};

const PageComponent: NextPage<IPricingPage> = ({ header, page, footer }) => {
  const [isGatewayInfoModalOpen, setIsGatewayInfoModalOpen] = useState(false);

  const {
    getGatewayPrice,
    // termLength,
    // setTermLength,
    gateway,
    setGateway,
    isFetchingCalculation,
  } = useContext(PricingContext);

  return (
    <>
      <Header
        pageTitle={page?.title}
        pageDescription={page?.description}
        {...header}
      />

      <Hero>
        <WidthDelimiter>
          <ResponsiveContainer>
            <Row>
              <Col md={8}>
                <HeroTitle
                  dangerouslySetInnerHTML={{
                    __html: page?.hero?.title ?? '',
                  }}
                />

                <HeroText>{page?.hero?.text}</HeroText>

                {/* <div style={{ maxWidth: 320, marginTop: 80 }}>
                  <HorizontalSelectInput
                    label="TERM LENGTH"
                    value={termLength}
                    onChange={(e) => setTermLength(e.target.value)}
                  >
                    <option value={ETermLength.MONTH}>Monthly</option>
                    <option value={ETermLength.YEAR} disabled={true}>
                      Yearly (free)
                    </option>
                  </HorizontalSelectInput>
                </div> */}
              </Col>
            </Row>
          </ResponsiveContainer>
        </WidthDelimiter>
      </Hero>

      <WidthDelimiter>
        <ResponsiveContainer>
          <BuildingQuantitySelector quantityText={page?.plans?.quantity_text} />

          <Plans>
            <Plan
              slug={EPlanSlug.STARTER}
              title={page.plans?.starter?.title!}
              description={page.plans?.starter?.text!}
              free={true}
            />

            <Plan
              slug={EPlanSlug.PRO}
              title={page.plans?.pro?.title!}
              description={page.plans?.pro?.text!}
            />

            <Plan
              slug={EPlanSlug.ENTERPRISE}
              title={page.plans?.enterprise?.title!}
              showPrice={false}
              description={page.plans?.enterprise?.text!}
              button={
                <Button
                  mType="primary"
                  mColor="teal"
                  onClick={() => Router.push('/contact')}
                >
                  {page.plans?.enterprise?.button?.title}
                </Button>
              }
            />
          </Plans>

          <div style={{ textAlign: 'center', marginTop: 50 }}>
            {page?.plans?.fess_disclaimer?.text && (
              <p style={{ marginBottom: 30 }}>
                {page?.plans?.fess_disclaimer?.text}
              </p>
            )}

            <Button
              mType="secondary"
              mColor="teal"
              onClick={() => Router.push('/pricing-features')}
            >
              COMPLETE FEATURE LIST
            </Button>
          </div>
        </ResponsiveContainer>
      </WidthDelimiter>

      <WidthDelimiter>
        <ResponsiveContainer>
          <GatewayCard>
            <div style={{ flex: 3 }}>
              <h2 className="title">{page?.gateway?.title}</h2>
              <p className="text">{page?.gateway?.text}</p>
            </div>

            <div>
              <h3 className="subtitle">
                {page?.gateway?.types_title}
                <Icon
                  name="info-circle"
                  size={18}
                  onClick={() => setIsGatewayInfoModalOpen(true)}
                />
              </h3>

              <ButtonGroup
                mColor="teal"
                selectedOption={gateway}
                disabled={isFetchingCalculation}
                onSelectOption={(o) => setGateway(o.id as any)}
                options={page?.gateway?.types_info?.types?.map((type) => ({
                  title: type?.title!,
                  id: type?.slug!,
                }))}
              />
            </div>

            <div style={{ flex: 1.5 }}>
              <h3 className="subtitle">Price</h3>

              {!isFetchingCalculation ? (
                <h4 className="price">{getPriceString(getGatewayPrice())}</h4>
              ) : (
                <div className="loading-indicator">
                  <CircularProgress
                    disableShrink={true}
                    thickness={7}
                    size={30}
                    style={{ color: 'white' }}
                  />
                </div>
              )}
            </div>
          </GatewayCard>

          <Modal
            isOpen={isGatewayInfoModalOpen}
            onRequestClose={() => setIsGatewayInfoModalOpen(false)}
          >
            <GatewayInfoModalContent>
              <h2>{page?.gateway?.types_info?.title}</h2>
              <p>{page?.gateway?.types_info?.text}</p>

              <Icon
                name="close"
                size={14}
                onClick={() => setIsGatewayInfoModalOpen(false)}
              />

              {page?.gateway?.types_info?.types?.map((type) => (
                <div className="type" key={type?.title}>
                  <strong>{type?.title}</strong>
                  <p>{type?.description}</p>
                </div>
              ))}

              <div className="footer">
                <span>{page?.gateway?.types_info?.footer_text}</span>
                <Button
                  mSize="medium"
                  mColor="teal"
                  mType="secondary"
                  onClick={() =>
                    Router.push(page?.gateway?.types_info?.footer_button?.href!)
                  }
                >
                  {page?.gateway?.types_info?.footer_button?.title}
                </Button>
              </div>
            </GatewayInfoModalContent>
          </Modal>
        </ResponsiveContainer>
      </WidthDelimiter>

      <WidthDelimiter style={{ display: 'none' }}>
        <ResponsiveContainer>
          <APICard>
            <div className="texts">
              <h3 className="title">{page?.api_credits?.title}</h3>
              <p
                className="text"
                dangerouslySetInnerHTML={{
                  __html: page?.api_credits?.text!,
                }}
              />
            </div>

            <ApiCreditsCalculator />
          </APICard>
        </ResponsiveContainer>
      </WidthDelimiter>

      <WidthDelimiter style={{ marginTop: 100 }}>
        <ResponsiveContainer>
          <Separator />

          <SectionTitle>{page?.highlights?.title}</SectionTitle>
          <SectionText
            style={{ maxWidth: 900 }}
            dangerouslySetInnerHTML={{
              __html: page?.highlights?.text!,
            }}
          />

          {page?.highlights?.itemss?.map((item, idx) => (
            <HighlightItem
              key={item?.title}
              className={classnames({
                mirrored: !(idx % 2 === 0),
              })}
            >
              <div className="image">
                <img
                  width={535}
                  height={229}
                  src={getAssetUrl(item?.imageConnection)}
                />
              </div>

              <div className="spacer" />

              <div className="texts">
                <SectionSubTitle>{item?.title}</SectionSubTitle>
                <SectionSubText
                  dangerouslySetInnerHTML={{
                    __html: item?.text!,
                  }}
                />
              </div>
            </HighlightItem>
          ))}
        </ResponsiveContainer>
      </WidthDelimiter>

      <WidthDelimiter>
        <ResponsiveContainer>
          <Separator />

          <SectionTitle>{page?.faq?.title}</SectionTitle>

          <FAQ
            items={page?.faq?.questions?.map((q) => ({
              question: q?.question!,
              answer: q?.answer!,
            }))}
          />
        </ResponsiveContainer>
      </WidthDelimiter>

      <Footer
        links={footer?.links}
        title={page?.footer?.title}
        text={page?.footer?.text}
        button={page?.footer?.button?.title}
        onButtonClick={() => Router.push(page?.footer?.button?.href ?? '')}
      />
    </>
  );
};

export const Plan: FunctionComponent<IPlan> = ({
  slug,
  title,
  description,
  free,
  buttonText,
  infoText,
  button,
  showPrice = true,
}) => {
  const { getPlanPrice, termLength, buildingQuantity, isFetchingCalculation } =
    useContext(PricingContext);

  const exceedsBuildingLimit = buildingQuantity > 1000;

  function renderPrice() {
    if (exceedsBuildingLimit) {
      return <h3 className="price words">Volume Discounts Apply</h3>;
    }

    return (
      <>
        {!isFetchingCalculation ? (
          <h3 className="price">
            {getPriceString(getPlanPrice(slug), true) || '$0'}
          </h3>
        ) : (
          <div className="loading-indicator">
            <CircularProgress
              disableShrink={true}
              thickness={6}
              size={50}
              style={{ color: 'white' }}
            />
          </div>
        )}

        <p className="term-length">
          {free ? 'Up to 5 buildings' : `per building/${termLength}`}
        </p>
      </>
    );
  }

  function renderButton() {
    if (button) {
      return button;
    }

    if (exceedsBuildingLimit) {
      return (
        <Button
          mType="primary"
          mColor="teal"
          onClick={() => Router.push(`/get-started?purpose=${slug}`)}
        >
          {!!buttonText ? buttonText : 'CONTACT US'}
        </Button>
      );
    }

    return (
      <Button
        mType="primary"
        mColor="teal"
        onClick={() => Router.push(`/signup?plan=${slug}`)}
      >
        {!!buttonText ? buttonText : 'GET STARTED'}
      </Button>
    );
  }

  return (
    <PlanCard className="plan-card">
      <h2 className="title">{title}</h2>

      {showPrice ? (
        renderPrice()
      ) : (
        <PlanCardPricePlaceholder small={exceedsBuildingLimit} />
      )}

      {!!infoText && <p className="text">{infoText}</p>}

      {renderButton()}

      {!!description && <p className="text">{description}</p>}
    </PlanCard>
  );
};

const BuildingQuantitySelector: FunctionComponent<{
  quantityText?: Maybe<string>;
}> = ({ quantityText }) => {
  const { buildingQuantity, setBuildingQuantity, isFetchingCalculation } =
    useContext(PricingContext);

  const [sliderValue, setSliderValue] = useState(buildingQuantity);

  return (
    <QuantitySelectionContainer>
      <div>
        <span>
          {quantityText}: <strong>{sliderValue}</strong>
        </span>
      </div>

      <SliderInput
        defaultValue={buildingQuantity}
        disabled={isFetchingCalculation}
        min={1}
        max={1001}
        step={10}
        onChange={(_, q) => setSliderValue(Number(q))}
        onChangeCommitted={(_, q) => setBuildingQuantity(Number(q))}
        marks={[10, 50, 100, 500, 1001].map((q) => ({
          value: q,
          label: q > 1000 ? '>1000' : q,
        }))}
      />
    </QuantitySelectionContainer>
  );
};

const ApiCreditsCalculator: FunctionComponent = () => {
  const [sliderValue, setSliderValue] = useState(million(5));

  const {
    isFetchingCalculation,
    termLength,
    apiCreditsQuantity,
    setApiCreditsQuantity,
    getApiCreditsPrice,
  } = useContext(PricingContext);

  const packPrice = getApiCreditsPrice();
  const paygPrice = getApiCreditsPrice(EApiPackageSlug.PAY_AS_YOU_GO);

  const savingPercentage = getSavingPercentage(
    paygPrice.fixedPrice,
    packPrice.fixedPrice + packPrice.overagePrice
  );

  const isGreaterThan50 = apiCreditsQuantity > million(50);

  function renderLoadingIndicator() {
    return (
      <div style={{ marginTop: 30 }}>
        <CircularProgress
          disableShrink={true}
          size={50}
          style={{ color: 'white' }}
        />
      </div>
    );
  }

  return (
    <div className="calculator">
      <h3 className="selected-valueage">
        API Usage: {numberToWord(sliderValue)} RU
      </h3>

      <SliderInput
        min={million(5)}
        max={million(55)}
        disabled={isFetchingCalculation}
        step={apiCreditsQuantity >= million(50) ? million(5) : million(1)}
        onChange={(_, val) => setSliderValue(Number(val))}
        onChangeCommitted={(_, val) => setApiCreditsQuantity(val as number)}
        marks={[
          million(5),
          million(10),
          million(20),
          million(50),
          million(55),
        ].map((p) => {
          let label = numberToWord(p);

          if (p > million(50)) {
            label = '>50M';
          }

          return {
            value: p,
            label,
          };
        })}
      />

      <div
        className={classnames({
          prices: true,
          isGreaterThan50,
        })}
      >
        <div className="price">
          <p className="type">Pay as you go</p>

          {isFetchingCalculation ? (
            renderLoadingIndicator()
          ) : (
            <>
              <strong className="total-amount">
                {getPriceString(paygPrice.fixedPrice, true)}
              </strong>
              <span className="unit-amount">
                {getPriceString(paygPrice.fixedPrice / apiCreditsQuantity)}/RU
                <br />
                per {termLength}
              </span>
            </>
          )}
        </div>

        <div className="price second">
          <p className="type">Monthly reserved</p>

          {isFetchingCalculation ? (
            renderLoadingIndicator()
          ) : (
            <>
              <strong className="total-amount">
                <span>{getPriceString(packPrice.fixedPrice, true)}</span>

                <span
                  style={{
                    display: !packPrice.overagePrice ? 'none' : undefined,
                  }}
                >
                  {' + ' + getPriceString(packPrice.overagePrice, true)}
                </span>
              </strong>

              {!!packPrice.overagePrice && (
                <span className="overage-disclaimer">Reserved + Overage</span>
              )}

              <span className="unit-amount">
                {getPriceString(
                  (packPrice.fixedPrice + packPrice.overagePrice) /
                    apiCreditsQuantity
                )}
                /RU
                <br />
                per {termLength}
              </span>

              <span
                className="saving"
                style={{ opacity: !!savingPercentage ? 1 : 0 }}
              >
                you save {savingPercentage}
              </span>
            </>
          )}
        </div>

        <div className="contact-us">
          <h3>Contact us</h3>
          <h3>for pricing on {'>'}50M RU</h3>
        </div>
      </div>
    </div>
  );
};

export async function getServerSideProps() {
  try {
    const { data: pageData } = await ContentStackClient().query<IPricingPage>({
      query: PAGE_QUERY,
    });

    const initialCalculations = await fetchCalculations(
      InitialCalculationsRequest
    );

    return {
      props: {
        ...pageData,
        initialCalculations,
      },
    };
  } catch (e) {
    return { props: {} };
  }
}

const PAGE_QUERY = gql`
  query pricingQuery {
    ${HEADER_QUERY}
    ${FOOTER_QUERY}

    page: pricing(uid: "blt44bd054a35e79931") {
      title
      description
      hero {
        title
        text
      }
      plans {
        starter {
          title
          text
        }
        pro {
          title
          text
        }
        enterprise {
          title
          text
          button {
            title
          }
        }
        quantity_text
        fess_disclaimer {
          text
          button {
            title
            href
          }
        }
      }
      gateway {
        title
        text
        types_title
        types_info {
          title
          text
          types {
            slug
            title
            description
          }
          footer_text
          footer_button {
            title
            href
          }
        }
      }
      api_credits {
        title
        text
      }
      highlights {
        title
        text
        itemss {
          title
          text
          imageConnection {
            edges {
              node {
                url
              }
            }
          }
        }
      }
      faq {
        title
        questions {
          question
          answer
        }
      }
      footer {
        title
        text
        button {
          title
          href
        }
      }
    }
  }
`;

const WidthDelimiter = styled.div`
  max-width: 1600px;
  position: relative;
  margin: 0 auto;
`;

const Hero = styled.div`
  width: 100%;
  padding-top: 160px;
  padding-bottom: 80px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: url(/img/home-glow-rectangle-1.svg) no-repeat bottom right;
  background-size: 80%;
`;

const HeroTitle = styled.h1`
  color: white;
  font-size: 67px;
  font-weight: bold;
  line-height: 1.19;
  margin-top: 0;

  strong {
    color: ${(props) => props.theme.colors.teal};
  }

  ${mediaScreen('xs', 'sm')} {
    font-size: 35px;
  }

  ${mediaScreen('sm', 'md')} {
    font-size: 50px;
  }
`;

const HeroText = styled.p`
  color: white;
  font-size: 22px;
  line-height: 1.5;
  margin-top: 50px;
  max-width: 85%;
  font-weight: 400;
  margin-bottom: 44px;

  ${mediaScreen('xs', 'sm')} {
    font-size: 17px;
    max-width: 100%;
    margin-top: 24px;
    margin-bottom: 32px;
  }

  ${mediaScreen('sm', 'md')} {
    font-size: 19px;
    margin-top: 24px;
    margin-bottom: 32px;
  }
`;

const QuantitySelectionContainer = styled.div`
  margin-bottom: 50px;

  > div:first-of-type {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 30px;

    span {
      font-size: 22px;
    }

    input {
      -moz-appearance: textfield;
      background: none;
      outline: none;
      font-size: 20px;
      padding: 5px 10px;
      text-align: center;
      width: 90px;
      border: 1px solid ${(props) => props.theme.colors.teal};

      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
    }
  }

  ${mediaScreen('xs', 'md')} {
    > div:first-of-type {
      span {
        font-size: 18px;
        flex: 1;
        display: block;
        text-align: center;
        max-width: 50%;

        strong {
          display: block;
          text-align: center;
          margin-top: 10px;
        }
      }
    }

    .MuiSlider-markLabel:nth-child(6),
    .MuiSlider-markLabel:nth-child(8) {
      display: none;
    }
  }
`;

const Card = styled.div`
  background: ${(props) => props.theme.colors.slate2};
  padding: 30px;
  color: white;

  .title {
    font-size: 38px;
    font-weight: 700;
    margin: 0;
  }

  .text {
    font-size: 16px;
  }

  ${mediaScreen('xs', 'md')} {
    .title {
      font-size: 29px;
    }

    .text {
      font-size: 14px;
    }
  }
`;

const Plans = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: space-between;

  ${mediaScreen('xs', 'md')} {
    display: block;
  }
`;

const PlanCard = styled(Card)`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px;
  width: 30%;

  .title {
    margin-bottom: 35px;
  }

  .loading-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 83px;
  }

  .price {
    font-size: 55px;
    font-weight: 700;
    margin: 0;

    &.words {
      font-size: 30px;
      margin-top: 10px;
      margin-bottom: 9px;
    }
  }

  .term-length {
    font-size: 16px;
    font-weight: 700;
    color: ${(props) => props.theme.colors.gray};
  }

  button {
    margin: 40px 0;
  }

  ${mediaScreen('xs', 'md')} {
    width: 100%;
    margin: 0 0 50px 0 !important;

    .title {
      margin-bottom: 20px;
    }

    .price {
      font-size: 45px;
      margin-bottom: 0 !important;
    }

    .term-length {
      font-size: 14px;
    }
  }
`;

const PlanCardPricePlaceholder = styled.div<{ small?: boolean }>`
  min-height: ${(props) => (props.small ? '64px' : '122.5px')};
`;

const GatewayCard = styled(Card)`
  margin-top: 100px;
  margin-bottom: 50px;
  display: flex;
  padding: 50px 30px;
  align-items: stretch;
  justify-content: space-around;

  > div {
    padding-right: 5%;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .subtitle {
    display: flex;
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    margin-bottom: 10px;

    .icon {
      opacity: 0.7;
      cursor: pointer;
      margin-left: 12px;
      position: relative;
      top: 3px;

      &:hover {
        opacity: 1;
      }
    }
  }

  .text {
    margin-top: 15px;
  }

  .loading-indicator {
    display: flex;
    align-items: center;
    height: 57px;
  }

  .price {
    font-size: 38px;
    font-weight: 700;
    margin: 0;
  }

  ${mediaScreen('xs', 'md')} {
    display: block;
    padding: 30px;

    > div:not(:first-of-type):not(:last-of-type) {
      margin: 30px 0;
    }

    .text {
      margin-top: 10px;
    }

    .subtitle {
      font-size: 14px;
      margin-bottom: 5px;

      .icon {
        display: none;
      }
    }

    .price {
      font-size: 20px;
    }
  }
`;

const GatewayInfoModalContent = styled.div`
  padding: 30px;
  max-width: 700px;
  position: relative;

  h2 {
    font-size: 30px;
    margin: 0 0 7px 0;
  }

  .icon {
    position: absolute;
    right: 30px;
    top: 30px;
    cursor: pointer;

    &:hover {
      color: ${(props) => props.theme.colors.teal};
    }
  }

  .type {
    margin-top: 30px;

    &:first-of-type {
      margin-top: 50px;
    }

    strong {
      display: block;
      text-transform: capitalize;
      color: white;
    }
  }

  .footer {
    text-align: center;
    margin-top: 60px;

    button {
      margin-left: 20px;
    }
  }
`;

const APICard = styled(Card)`
  display: flex;

  .title {
    margin-bottom: 40px;
    margin-top: 30px;
  }

  .texts {
    width: 30%;
    padding-right: 100px;
  }

  .calculator {
    width: 70%;

    .selected-valueage {
      font-size: 22px;
      font-weight: 700;
      margin-top: 0;
      margin-bottom: 15px;
    }

    .prices {
      display: flex;
      width: 100%;
      min-height: 282px;
      padding: 50px 0;
      position: relative;
      margin-top: 50px;
      align-items: stretch;
      justify-content: space-around;
      background-image: linear-gradient(
          to left,
          rgba(38, 47, 64, 0),
          #262f40,
          rgba(38, 47, 64, 0)
        ),
        linear-gradient(to right, #191f2c, #191f2c);

      .price {
        display: flex;
        flex: 1;
        text-align: center;
        flex-direction: column;
        align-items: center;

        .type {
          font-size: 16px;
          border-bottom: 1px solid white;
          padding-bottom: 20px;
          margin-bottom: 20px;
          width: 50%;
        }

        .total-amount {
          font-size: 30px;
          font-weight: 700;
          margin-bottom: 0;
        }

        .overage-disclaimer {
          color: ${(props) => props.theme.colors.yellow};
        }

        .unit-amount {
          font-size: 14px;
        }

        .saving {
          font-size: 14px;
          margin-top: -21px;
          position: relative;
          top: 30px;
          color: ${(props) => props.theme.colors.teal};
        }
      }

      .contact-us {
        display: none;
        position: absolute;
        top: 0;
        right: 0;
        left: 0;
        bottom: 0;
        flex-direction: column;
        align-items: center;
        justify-content: flex-end;
        padding-bottom: 50px;

        h3 {
          font-size: 28px;
          margin: 0;

          &:first-of-type {
            margin-bottom: 10px;
            color: ${(props) => props.theme.colors.teal};
          }
        }
      }

      &.isGreaterThan50 {
        .contact-us {
          display: flex;
        }

        .price {
          opacity: 0.2;
        }

        .total-amount,
        .overage-disclaimer,
        .unit-amount,
        .saving {
          display: none;
        }
      }
    }
  }

  ${mediaScreen('xs', 'md')} {
    display: block;

    > div {
      width: 100% !important;
      padding: 0 !important;
    }

    .title {
      margin: 0;
      margin-bottom: 15px;
    }

    .calculator {
      margin-top: 30px;

      .prices {
        display: block;

        .price.second {
          margin-top: 80px !important;

          .saving {
            margin-top: 10px !important;
            top: 0;
          }
        }

        .contact-us {
          position: relative;
          padding-bottom: 0;

          h3 {
            font-size: 22px;
          }
        }
      }

      .MuiSlider-markLabel:nth-child(6),
      .MuiSlider-markLabel:nth-child(10) {
        display: none;
      }
    }
  }
`;

const SectionTitle = styled.h2`
  font-size: 50px;
  line-height: 1.2;
  color: white;
  font-weight: bold;
  margin-top: 0;

  ${mediaScreen('xs', 'sm')} {
    font-size: 29px;
  }

  ${mediaScreen('sm', 'md')} {
    font-size: 40px;
  }
`;

const SectionSubTitle = styled.h3`
  margin-top: 0;
  margin-bottom: 25px;
  font-size: 24px;
  line-height: 1.16;
  color: white;
  font-weight: bold;

  ${mediaScreen('xs', 'sm')} {
    font-size: 18px;
    margin-bottom: 15px;
  }
`;

const SectionText = styled.p`
  color: white;
  font-size: 22px;
  line-height: 30px;

  ${mediaScreen('xs', 'sm')} {
    font-size: 17px;
    line-height: 24px;
  }

  ${mediaScreen('sm', 'md')} {
    font-size: 19px;
    line-height: 25px;
  }
`;

const SectionSubText = styled.p`
  color: white;
  font-size: 16px;
  line-height: 1.5;

  ${mediaScreen('xs', 'sm')} {
    font-size: 14px;
  }

  ${mediaScreen('sm', 'md')} {
    font-size: 16px;
  }
`;

const HighlightItem = styled.div`
  width: 100%;
  display: flex;
  padding-top: 100px;
  padding-bottom: 50px;

  .image {
    flex: 1.2;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 100%;
    }
  }

  .spacer {
    flex: 0.2;
  }

  .texts {
    flex: 1;
    justify-content: center;
    display: flex;
    text-align: left;
    flex-direction: column;
  }

  &.mirrored {
    flex-direction: row-reverse;
  }

  &:last-of-type {
    margin-bottom: 100px;
  }

  ${mediaScreen('xs', 'md')} {
    flex-direction: column !important;
    padding-top: 50px;
    padding-bottom: 25px;

    &:last-of-type {
      margin-bottom: 50px;
    }

    .spacer {
      padding-top: 20px;
    }
  }
`;

interface IPlan {
  slug: EPlanSlug;
  title: string;
  description?: string;
  free?: boolean;
  buttonText?: string;
  infoText?: string;
  button?: ReactNode;
  showPrice?: boolean;
}

interface IPricingPage extends IPageProps<Pricing> {
  initialCalculations: ICalculation[];
}

export default Page;
